import React, { useLayoutEffect, useRef, useState } from 'react';
import { Alert, Avatar, Box, Button, CircularProgress, Paper, Stack, TextField, Typography } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import { useBoardChat, useSendChatMessage } from '../../data/chat';
import { MESSAGE_MAX_LENGTH } from '../../contract/chat';
import { apiErrorMessage } from '../../utilities/apiError';

export function BoardChat({ boardId }: { boardId: string }) {
    const chat = useBoardChat(boardId);
    const send = useSendChatMessage(boardId);
    const [draft, setDraft] = useState('');
    const scroll = useRef<HTMLDivElement>(null);
    const follow = useRef(true);
    const olderHeight = useRef<number | null>(null);
    const messages = [...(chat.data?.pages ?? [])].reverse().flatMap((page) => page.messages);
    // Polling never pulls someone away from the older messages they are reading.
    useLayoutEffect(() => {
        const el = scroll.current;
        if (!el) return;
        if (olderHeight.current !== null && !chat.isFetchingNextPage) {
            el.scrollTop += el.scrollHeight - olderHeight.current;
            olderHeight.current = null;
        } else if (follow.current) el.scrollTop = el.scrollHeight;
    }, [chat.data, chat.isFetchingNextPage]);

    const submit = async (event: React.FormEvent) => {
        event.preventDefault();
        if (!draft.trim() || send.isPending) return;
        try {
            await send.mutateAsync(draft.trim());
            setDraft('');
            follow.current = true;
        } catch { /* Keep the draft so it can be retried. */ }
    };

    return (
        <Paper variant="outlined" sx={{ maxWidth: 900, mx: 'auto', overflow: 'hidden' }}>
            <Box sx={{ px: 2.5, py: 2, borderBottom: 1, borderColor: 'divider' }}>
                <Typography component="h2" variant="h6">Board chat</Typography>
                <Typography variant="body2" color="text.secondary">A shared conversation for members of this board. Updates every 5 seconds.</Typography>
            </Box>
            {chat.isError && <Alert severity="error" action={<Button onClick={() => chat.refetch()}>Retry</Button>}>{apiErrorMessage(chat.error, 'Could not load messages.')}</Alert>}
            <Box ref={scroll} onScroll={() => {
                const el = scroll.current;
                if (el) follow.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
            }} sx={{ height: { xs: '45vh', sm: 420 }, overflowY: 'auto', p: 2.5 }}>
                {chat.isPending && <Box sx={{ textAlign: 'center', py: 5 }}><CircularProgress size={28} aria-label="Loading messages" /></Box>}
                {chat.hasNextPage && <Button fullWidth disabled={chat.isFetchingNextPage} onClick={() => { follow.current = false; olderHeight.current = scroll.current?.scrollHeight ?? null; void chat.fetchNextPage(); }}>Load older messages</Button>}
                {!chat.isPending && !chat.isError && messages.length === 0 && <Stack alignItems="center" spacing={1} sx={{ py: 6, color: 'text.secondary' }}>
                    <ForumOutlinedIcon sx={{ fontSize: 36 }} />
                    <Typography>No messages yet</Typography>
                    <Typography variant="body2">Start the conversation with your board.</Typography>
                </Stack>}
                <Stack spacing={2.5} role="list" aria-label="Board messages">
                    {messages.map((message) => <Stack key={message.id} direction="row" spacing={1.5} role="listitem">
                        <Avatar aria-hidden sx={{ width: 32, height: 32, fontSize: 14, bgcolor: 'primary.main' }}>{message.authorName.charAt(0).toUpperCase()}</Avatar>
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Stack direction="row" spacing={1} alignItems="baseline" flexWrap="wrap">
                                <Typography variant="body2" fontWeight={600} sx={{ overflowWrap: 'anywhere', minWidth: 0 }}>{message.authorName}</Typography>
                                <Typography component="time" dateTime={message.createdAt} variant="caption" color="text.secondary">{new Date(message.createdAt).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</Typography>
                            </Stack>
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', mt: 0.5 }}>{message.text}</Typography>
                        </Box>
                    </Stack>)}
                </Stack>
            </Box>
            <Box component="form" onSubmit={submit} sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
                {send.isError && <Alert severity="error" sx={{ mb: 1.5 }}>{apiErrorMessage(send.error, 'Message could not be sent. Your draft is saved here.')}</Alert>}
                <TextField label="Message your board" placeholder="Write a message…" fullWidth multiline minRows={2} maxRows={6}
                    value={draft} disabled={send.isPending || chat.isError || chat.isPending}
                    onChange={(event) => setDraft(event.target.value)}
                    slotProps={{ htmlInput: { maxLength: MESSAGE_MAX_LENGTH } }}
                    helperText={`${draft.length}/${MESSAGE_MAX_LENGTH} · Only board members can read this conversation.`} />
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                    <Button type="submit" variant="contained" endIcon={<SendIcon />} disabled={!draft.trim() || send.isPending || chat.isError || chat.isPending}>{send.isPending ? 'Sending…' : 'Send'}</Button>
                </Box>
            </Box>
        </Paper>
    );
}
